import React from 'react';
import { useTranslation } from 'react-i18next';

import { BarChart3, ChevronDown, FolderPlus, ListFilter, Plus, Star } from 'lucide-react';

import { useDrawingsEnabled } from '../../hooks/useDrawingsEnabled';
import { greyP5 } from '../../theme.ts';

export type DashboardTab = 'projects' | 'drawings' | 'catalogue' | 'exports' | 'analytics';

interface HeaderProps {
  activeTab?: DashboardTab;
  onTabChange?: (tab: DashboardTab) => void;
  showCatalogueTab?: boolean;
  showAnalyticsTab?: boolean;
  viewMode?: 'grid' | 'list';
  onViewModeChange?: (mode: 'grid' | 'list') => void;
  gridSize?: 'small' | 'medium' | 'large';
  onGridSizeChange?: (size: 'small' | 'medium' | 'large') => void;
  sortBy?: string;
  onSortChange?: (sort: string) => void;
  onCreateProject?: () => void;
  onCreateFolder?: () => void;
  onCreateDrawing?: () => void;
  onCreateCatalogue?: () => void;
  isCatalogueSelected?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  activeTab = 'projects',
  onTabChange,
  showCatalogueTab = true,
  showAnalyticsTab = false,
  viewMode = 'grid',
  onViewModeChange,
  gridSize = 'medium',
  onGridSizeChange,
  sortBy = 'Date created',
  onSortChange,
  onCreateProject,
  onCreateFolder,
  onCreateDrawing,
  onCreateCatalogue,
  isCatalogueSelected,
}) => {
  const { t } = useTranslation();
  const showDrawingsTab = useDrawingsEnabled();
  const [showSortDropdown, setShowSortDropdown] = React.useState(false);
  const [showPlusDropdown, setShowPlusDropdown] = React.useState(false);

  const sortOptions = [
    { value: 'Date created', label: t('projects.dateCreated') },
    { value: 'Date modified', label: t('projects.dateModified') },
    { value: 'Name A-Z', label: t('projects.nameAZ') },
    { value: 'Name Z-A', label: t('projects.nameZA') },
  ];

  return (
    <header className="sticky top-0 w-full z-30">
      <div className="h-16 bg-black flex items-center border-b md:overflow-visible" style={{ borderColor: greyP5 }}>
        <div className="hidden md:flex items-center justify-start px-4 w-auto md:w-[240px]">
          <span className="text-greyP6 text-xl font-normal family-azbuka">{t('projects.dashboard')}</span>
        </div>

        <div className="flex items-center justify-between flex-1 px-2 sm:px-4 overflow-visible">
          <div className="flex items-center flex-shrink-0">
            {/* Mobile button for new project/folder/catalogue - on the left */}
            <div className="flex md:hidden items-center gap-2 mr-1 sm:mr-2 relative">
              {activeTab === 'projects' && (
                <>
                  <button
                    onClick={() => setShowPlusDropdown(!showPlusDropdown)}
                    className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 bg-white text-black rounded-full hover:bg-gray-100 transition-colors duration-200"
                    title={t('projects.sidebar.newProject')}
                  >
                    <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>

                  {showPlusDropdown && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowPlusDropdown(false)} />
                      <div className="absolute left-0 top-full mt-2 w-48 bg-greyP5 rounded-lg shadow-xl py-2 z-50">
                        {onCreateProject && (
                          <button
                            onClick={() => {
                              onCreateProject();
                              setShowPlusDropdown(false);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-white hover:bg-greyP4 flex items-center gap-2"
                          >
                            <Plus className="w-4 h-4" />
                            {t('projects.sidebar.newProject')}
                          </button>
                        )}
                        {onCreateFolder && (
                          <button
                            onClick={() => {
                              onCreateFolder();
                              setShowPlusDropdown(false);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-white hover:bg-greyP4 flex items-center gap-2"
                          >
                            <FolderPlus className="w-4 h-4" />
                            {t('projects.sidebar.newFolder')}
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}

              {activeTab === 'drawings' && onCreateDrawing && (
                <button
                  onClick={onCreateDrawing}
                  className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 bg-white text-black rounded-full hover:bg-gray-100 transition-colors duration-200"
                  title={t('drawings.newDrawing')}
                >
                  <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              )}

              {activeTab === 'catalogue' && (
                <button
                  onClick={() => onCreateCatalogue?.()}
                  className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 bg-white text-black rounded-full hover:bg-gray-100 transition-colors duration-200"
                  title={isCatalogueSelected ? t('catalogue.addSeries') : t('catalogue.newManufacturer')}
                >
                  <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              )}
            </div>

            <div className="flex items-center bg-blackP1 border border-greyP5 rounded-full mr-1 sm:mr-2 md:mr-4">
              <button
                onClick={() => onTabChange?.('projects')}
                className={`w-[70px] xs:w-[80px] sm:w-[100px] md:w-[160px] px-2 sm:px-3 md:px-6 py-2 font-medium rounded-full shadow-md transition-colors duration-200 text-[10px] xs:text-xs sm:text-sm md:text-base ${
                  activeTab === 'projects' ? 'bg-white text-black' : 'bg-transparent text-greyP3 hover:text-white'
                }`}
              >
                {t('projects.projects')}
              </button>
              {showDrawingsTab && (
                <button
                  onClick={() => onTabChange?.('drawings')}
                  className={`w-[70px] xs:w-[80px] sm:w-[100px] md:w-[160px] px-2 sm:px-3 md:px-6 py-2 font-medium rounded-full shadow-md transition-colors duration-200 text-[10px] xs:text-xs sm:text-sm md:text-base ${
                    activeTab === 'drawings' ? 'bg-white text-black' : 'bg-transparent text-greyP3 hover:text-white'
                  }`}
                >
                  {t('drawings.tab')}
                </button>
              )}
              {showCatalogueTab && (
                <button
                  onClick={() => onTabChange?.('catalogue')}
                  className={`w-[70px] xs:w-[80px] sm:w-[100px] md:w-[160px] px-2 sm:px-3 md:px-6 py-2 font-medium rounded-full shadow-md transition-colors duration-200 text-[10px] xs:text-xs sm:text-sm md:text-base ${
                    activeTab === 'catalogue' ? 'bg-white text-black' : 'bg-transparent text-greyP3 hover:text-white'
                  }`}
                >
                  {t('projects.catalogue')}
                </button>
              )}
              {showAnalyticsTab && (
                <button
                  onClick={() => onTabChange?.('analytics')}
                  className={`flex items-center justify-center gap-1.5 w-[70px] xs:w-[80px] sm:w-[100px] md:w-[160px] px-2 sm:px-3 md:px-6 py-2 font-medium rounded-full shadow-md transition-colors duration-200 text-[10px] xs:text-xs sm:text-sm md:text-base ${
                    activeTab === 'analytics' ? 'bg-white text-black' : 'bg-transparent text-greyP3 hover:text-white'
                  }`}
                  title={t('analytics.navTitle', 'Analytics')}
                >
                  <BarChart3 className="w-3 h-3 md:w-4 md:h-4" />
                  <span className="hidden xs:inline">{t('analytics.navTitle', 'Analytics')}</span>
                </button>
              )}
            </div>

            {activeTab === 'catalogue' && (
              <a
                href="#favorites"
                className="hidden md:flex items-center gap-1.5 px-3 py-2 rounded-full text-sm text-greyP3 hover:text-white hover:bg-greyP5 transition-colors duration-200"
                title={t('catalogue.favorites')}
              >
                <Star className="w-4 h-4" />
                <span>{t('catalogue.favorites')}</span>
              </a>
            )}
          </div>

          <div className="flex items-center flex-shrink-0">
            {activeTab !== 'catalogue' && viewMode === 'grid' && (
              <div className="hidden md:flex items-center bg-black border border-greyP4 rounded-full mr-4">
                <button
                  onClick={() => onGridSizeChange?.('small')}
                  className={`flex items-center justify-center w-10 h-8 rounded-l-full transition-colors duration-200 ${
                    gridSize === 'small' ? 'bg-greyP4' : 'bg-transparent hover:bg-blackP1'
                  }`}
                >
                  <div className="grid grid-cols-3 gap-0.5">
                    {[...Array(9)].map((_, i) => (
                      <div
                        key={i}
                        className={`w-1 h-1 border transition-colors duration-200 ${
                          gridSize === 'small' ? 'border-green01' : 'border-greyP4'
                        }`}
                      />
                    ))}
                  </div>
                </button>

                <button
                  onClick={() => onGridSizeChange?.('medium')}
                  className={`flex items-center justify-center w-10 h-8 transition-colors duration-200 ${
                    gridSize === 'medium' ? 'bg-greyP4' : 'bg-transparent hover:bg-blackP1'
                  }`}
                >
                  <div className="grid grid-cols-2 gap-0.5">
                    {[...Array(4)].map((_, i) => (
                      <div
                        key={i}
                        className={`w-1.5 h-1.5 border transition-colors duration-200 ${
                          gridSize === 'medium' ? 'border-green01' : 'border-greyP4'
                        }`}
                      />
                    ))}
                  </div>
                </button>

                <button
                  onClick={() => onGridSizeChange?.('large')}
                  className={`flex items-center justify-center w-10 h-8 rounded-r-full transition-colors duration-200 ${
                    gridSize === 'large' ? 'bg-greyP4' : 'bg-transparent hover:bg-blackP1'
                  }`}
                >
                  <div className="grid grid-cols-1 gap-0.5">
                    {[...Array(1)].map((_, i) => (
                      <div
                        key={i}
                        className={`w-2 h-2 border transition-colors duration-200 ${
                          gridSize === 'large' ? 'border-green01' : 'border-greyP4'
                        }`}
                      />
                    ))}
                  </div>
                </button>
              </div>
            )}
            {activeTab !== 'catalogue' && (
              // With the Drawings tab there is no room for it on a phone.
              <div
                className={`${showDrawingsTab ? 'hidden sm:flex' : 'flex'} items-center bg-black border border-greyP4 rounded-full mr-2 md:mr-4`}
              >
                <button
                  onClick={() => onViewModeChange?.('grid')}
                  className={`flex items-center justify-center w-8 md:w-10 h-8 rounded-l-full transition-colors duration-200 ${
                    viewMode === 'grid' ? 'bg-greyP4' : 'bg-transparent hover:bg-blackP1'
                  }`}
                >
                  <div className="grid grid-cols-2 gap-0.5">
                    {[...Array(4)].map((_, i) => (
                      <div
                        key={i}
                        className={`w-1.5 h-1.5 border transition-colors duration-200 ${
                          viewMode === 'grid' ? 'border-green01' : 'border-greyP4'
                        }`}
                      />
                    ))}
                  </div>
                </button>

                <button
                  onClick={() => onViewModeChange?.('list')}
                  className={`flex items-center justify-center w-8 md:w-10 h-8 rounded-r-full transition-colors duration-200 ${
                    viewMode === 'list' ? 'bg-greyP4' : 'bg-transparent hover:bg-blackP1'
                  }`}
                >
                  <div className="flex flex-col space-y-0.5">
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className={`w-4 h-0.5 transition-colors duration-200 ${
                          viewMode === 'list' ? 'bg-green01' : 'bg-greyP4'
                        }`}
                      />
                    ))}
                  </div>
                </button>
              </div>
            )}

            {(activeTab === 'projects' || activeTab === 'drawings') && (
              <div className="relative">
                <button
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  className="flex min-w-max items-center justify-center md:justify-between p-2 md:p-2 border border-greyP4 rounded-full text-white hover:text-white transition-colors duration-200"
                >
                  <ListFilter className="w-4 h-4 md:hidden" />
                  <span className="hidden md:inline ml-1 text-sm text-greyP3 whitespace-nowrap">
                    {t('projects.sort')}:
                  </span>
                  <span className="hidden md:inline text-sm ml-1 whitespace-nowrap">
                    {sortOptions.find((opt) => opt.value === sortBy)?.label || sortBy}
                  </span>
                  <ChevronDown className="hidden md:block w-4 h-4 ml-1 mr-1" />
                </button>

                {showSortDropdown && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowSortDropdown(false)} />

                    <div className="absolute right-0 top-full mt-2 w-48 bg-greyP5 rounded-lg shadow-xl py-2 z-50">
                      {sortOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            onSortChange?.(option.value);
                            setShowSortDropdown(false);
                          }}
                          className={`w-full px-4 py-2 text-left text-sm transition-colors duration-200 ${
                            sortBy === option.value ? 'text-green01 bg-greyP4' : 'text-white hover:bg-greyP4'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
